import * as React from 'react'
import * as _ from 'underscore'
import { Trace } from '../../domain/Trace'
import { FunctionCall } from '../../helper/FunctionCall'
import Memory from '../../helper/memory'
import Milliseconds from '../../helper/milliseconds'
import Percent from '../../helper/percent'
import { Box, BoxBody, BoxHeader } from '../../ui/box'
import { selectXhprofCalls } from '../../xhprof/XhprofProfiler'
import NamespaceSelector from '../namespace'

interface Props {
    startTracepointEvent: { startTime: string; duration: string } | null
    trace: Trace
    criteria: CallsViewCriteria
    selectFunction: (call: string) => void
    router: any
    clickRoute: string
    changeSearchString: (searchString: string) => void
    commitSearchString: (ev) => void
    setDefaultCall?: (call: string) => void
    params: any
    collapsable: boolean
    onCollapseChange?: (collapsed: boolean) => void
}
interface State {
    searchString: string
    expanded: boolean
}

export interface CallsViewCriteria {
    filters: string[]
    sortBy: string
    page: number
    compareMode: boolean
    showMemory: boolean
    query?: string
    fn?: string
}

export const createCallsViewCriteria = (params, searchString?: string): CallsViewCriteria => {
    const { sortBy = 'excl_wt', page = 1, filters = [], fn, base, profile } = params

    let { query } = params

    if (searchString !== undefined) {
        query = searchString
    }

    const compareMode = base !== profile
    const showMemory = sortBy === 'excl_pmu' || sortBy === 'pmu'

    return {
        sortBy,
        page,
        filters,
        query,
        compareMode,
        fn,
        showMemory,
    }
}

export class CallsView extends React.PureComponent<Props, State> {
    public static defaultProps = {
        collapsable: false,
    }
    private static ConfigKey = 'callgraph:calls-view-expanded'

    constructor(props) {
        super(props)

        let expanded: boolean = true
        if (this.props.collapsable) {
            const savedSettings = localStorage.getItem(CallsView.ConfigKey)

            if (savedSettings) {
                expanded = Boolean(JSON.parse(savedSettings))

                if (this.props.onCollapseChange) {
                    this.props.onCollapseChange(!expanded)
                }
            }
        }

        this.state = { searchString: props.criteria.query || '', expanded }
    }

    public render = () => {
        const { startTracepointEvent, criteria, trace, selectFunction, commitSearchString, setDefaultCall } = this.props
        const { expanded } = this.state

        if (!trace.xhprofReport) {
            return
        }

        const { data: callList, hasMemory } = selectXhprofCalls(trace.xhprofReport.data, criteria)

        let selectedCall = criteria.fn
        if (!selectedCall && callList && Object.keys(callList).length) {
            selectedCall = Object.keys(callList)[0]
            if (setDefaultCall) {
                setDefaultCall(selectedCall)
            }
        }

        if (!expanded) {
            return (
                <Box className="call-list" onClick={this.toggleCollapse}>
                    <BoxHeader className="tw-p-8">
                        <button className="btn btn-default btn-xs" onClick={this.toggleCollapse}>
                            <i className="fa fa-plus" />
                        </button>
                    </BoxHeader>
                    <BoxHeader className="rotated tw-p-8">
                        <div className="expand">
                            <a onClick={this.toggleCollapse}>
                                <i className="fa fa-angle-double-down" /> all calls <i className="fa fa-angle-double-down" />
                            </a>
                        </div>
                    </BoxHeader>
                </Box>
            )
        }

        return (
            <Box className="call-list">
                <BoxHeader className="tw-p-8">
                    <div className="row">
                        <div className="col-md-6">
                            {this.props.collapsable && (
                                <div className="tw-mr-8" style={{ float: 'left' }}>
                                    <button className="btn btn-default btn-xs" onClick={this.toggleCollapse}>
                                        <i className="fa fa-minus" />
                                    </button>
                                </div>
                            )}
                            {!hasMemory ? null : (
                                <div>
                                    <div className="btn-group">
                                        <button
                                            type="button"
                                            className={criteria.showMemory ? 'btn btn-xs btn-default' : 'btn btn-xs btn-default active'}
                                            onClick={this.onTimeClick}
                                        >
                                            Time
                                        </button>
                                        <button
                                            type="button"
                                            className={criteria.showMemory ? 'btn btn-xs btn-default active' : 'btn btn-xs btn-default'}
                                            onClick={this.onMemoryClick}
                                        >
                                            Memory
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="col-md-6 align-right">
                            <form onSubmit={commitSearchString} role="search">
                                <div className="input-group">
                                    <input
                                        type="text"
                                        className="form-control input-sm"
                                        value={this.state.searchString}
                                        onChange={this.handleSearchStringChange}
                                        placeholder="Search"
                                    />
                                    <span className="input-group-btn">
                                        <button type="submit" className="btn btn-sm btn-default">
                                            <span className="fa fa-search" />
                                        </button>
                                    </span>
                                </div>
                            </form>
                        </div>
                    </div>
                    <NamespaceSelector
                        namespaces={trace.xhprofReport.namespaces}
                        filter={criteria.filters}
                        onClick={this.handleNamespaceClick}
                        totalWalltime={trace.xhprofReport.overallTotals.wt}
                    />
                    {criteria.filters.length > 0 || this.state.searchString.length > 0 ? (
                        <div className="text-right tw-mb-8">
                            <a onClick={this.handleClearSelectionClick} className="text-link">
                                <i className="fa fa-times" /> clear filter
                            </a>
                        </div>
                    ) : null}
                    {startTracepointEvent ? (
                        <div className="alert alert-info tw-m-0 tw-mb-8 tw-p-12">
                            <span>
                                <i className="fa fa-info-circle" /> This Callgraph only covers the{' '}
                                <b>last {startTracepointEvent.duration} ms</b> of this trace. See{' '}
                                <a
                                    target="_blank"
                                    href="https://support.tideways.com/documentation/setup/configuration/tracepoints.html?utm_medium=app&utm_source=callgraph&utm_campaign=partial-tracepoints#tracepoints-and-partial-callgraphs-explained"
                                    rel="noreferrer"
                                >
                                    documentation
                                </a>{' '}
                                for details.
                            </span>
                        </div>
                    ) : null}
                </BoxHeader>
                <BoxBody noPadding>
                    <table className="table table-striped table-bordered calls">
                        <thead>
                            <tr>
                                <th>Function Name</th>
                                <th style={{ width: '10%' }}>
                                    <a
                                        className="pull-right"
                                        onClick={() => {
                                            this.setQueryParameter({ sortBy: 'ct' })
                                        }}
                                    >
                                        Calls <span className={criteria.sortBy === 'ct' ? 'fa fa-sort-desc' : 'fa fa-sort text-mute'} />
                                    </a>
                                </th>
                                {criteria.showMemory ? (
                                    <th style={{ width: '140' }}>
                                        <a
                                            className="pull-right"
                                            onClick={() => {
                                                this.setQueryParameter({ sortBy: 'pmu' })
                                            }}
                                        >
                                            Total{' '}
                                            <span className={criteria.sortBy === 'pmu' ? 'fa fa-sort-desc' : 'fa fa-sort text-mute'} />
                                        </a>
                                    </th>
                                ) : (
                                    <th style={{ width: '12%' }}>
                                        <a
                                            className="pull-right"
                                            onClick={() => {
                                                this.setQueryParameter({ sortBy: 'wt' })
                                            }}
                                        >
                                            Total <span className={criteria.sortBy === 'wt' ? 'fa fa-sort-desc' : 'fa fa-sort text-mute'} />
                                        </a>
                                    </th>
                                )}
                                {criteria.showMemory ? (
                                    <th style={{ width: '12%' }}>
                                        <a
                                            className="pull-right"
                                            onClick={() => {
                                                this.setQueryParameter({ sortBy: 'excl_pmu' })
                                            }}
                                        >
                                            Self{' '}
                                            <span className={criteria.sortBy === 'excl_pmu' ? 'fa fa-sort-desc' : 'fa fa-sort text-mute'} />
                                        </a>
                                    </th>
                                ) : (
                                    <th style={{ width: '12%' }}>
                                        <a
                                            className="pull-right"
                                            onClick={() => {
                                                this.setQueryParameter({ sortBy: 'excl_wt' })
                                            }}
                                        >
                                            Self{' '}
                                            <span className={criteria.sortBy === 'excl_wt' ? 'fa fa-sort-desc' : 'fa fa-sort text-mute'} />
                                        </a>
                                    </th>
                                )}
                            </tr>
                        </thead>
                        <tbody>
                            {_.map(callList, (data, name) => (
                                <tr key={name} className={this.classesForCallRow(data, name === selectedCall)}>
                                    <td>
                                        <a onClick={() => selectFunction(name)}>
                                            <FunctionCall maxWidth={350} call={name} />
                                        </a>
                                    </td>
                                    <td className="number">
                                        {criteria.compareMode && data.ct === 0 ? <span>&plusmn;</span> : ''} {data.ct}
                                    </td>
                                    <td className="number">
                                        {criteria.showMemory ? (
                                            <Memory value={data.pmu} showPlusSign={criteria.compareMode} />
                                        ) : (
                                            <Milliseconds value={data.wt} showPlusSign={criteria.compareMode} showNoValueAsHyphen={false} />
                                        )}
                                        <br />
                                        <span className="text-mute">
                                            <Percent
                                                value={((criteria.showMemory ? data.perc_pmu : data.perc_wt) ?? 0) / 100}
                                                showPlusSign={criteria.compareMode}
                                            />
                                        </span>
                                    </td>
                                    <td className="number">
                                        {criteria.showMemory ? (
                                            <Memory value={data.excl_pmu} showPlusSign={criteria.compareMode} />
                                        ) : (
                                            <Milliseconds
                                                value={data.excl_wt}
                                                showPlusSign={criteria.compareMode}
                                                showNoValueAsHyphen={false}
                                            />
                                        )}
                                        <br />
                                        <span className="text-mute">
                                            <Percent
                                                value={((criteria.showMemory ? data.perc_excl_pmu : data.perc_excl_wt) ?? 0) / 100}
                                                showPlusSign={criteria.compareMode}
                                            />
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    <nav>
                        <ul className="pager">
                            <li className={(1 * criteria.page <= 1 ? 'disabled' : null) + ' previous'}>
                                <a
                                    onClick={() => {
                                        this.setQueryParameter({ page: Math.max(1, 1 * criteria.page - 1) })
                                    }}
                                >
                                    Previous
                                </a>
                            </li>
                            <li className="next">
                                <a
                                    onClick={() => {
                                        this.setQueryParameter({ page: 1 * criteria.page + 1 })
                                    }}
                                >
                                    Next
                                </a>
                            </li>
                        </ul>
                    </nav>
                </BoxBody>
            </Box>
        )
    }

    private setQueryParameter = values => {
        this.props.router.goTo(this.props.clickRoute, { ...this.props.params, ...values })
    }

    private classesForCallRow = (data, isSelectedCall) => {
        const classes: string[] = []

        if (data.perc_excl_wt > 50) {
            classes.push('danger')
        } else if (data.perc_excl_wt > 10) {
            classes.push('warning')
        }

        if (isSelectedCall) {
            classes.push('selected')
        }

        return classes.join(' ')
    }

    private onMemoryClick = evt => {
        this.setQueryParameter({ sortBy: 'excl_pmu', page: 1 })
    }

    private onTimeClick = evt => {
        this.setQueryParameter({ sortBy: 'excl_wt', page: 1 })
    }

    private handleSearchStringChange = ev => {
        if (this.props.criteria.page !== 1) {
            this.setQueryParameter({ page: 1 })
        }

        this.setState({ searchString: ev.target.value })
        this.props.changeSearchString(ev.target.value)
    }

    private handleNamespaceClick = namespace => {
        let { filters } = this.props.criteria

        filters = _.contains(filters, namespace) ? filters.filter(element => element !== namespace) : [...filters, namespace]

        this.setQueryParameter({ filters, page: 1 })
    }

    private handleClearSelectionClick = () => {
        this.setState({ searchString: '' })
        this.setQueryParameter({ filters: null, page: 1 })
    }

    private toggleCollapse = () => {
        const newState = !this.state.expanded

        this.setState({ expanded: newState })

        if (this.props.onCollapseChange) {
            this.props.onCollapseChange(!newState)
        }

        localStorage.setItem(CallsView.ConfigKey, JSON.stringify(newState))
    }
}
